import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { AppIcon } from '../ui/icon'
import { mdiDownload } from '@mdi/js'
import { Button } from '../ui/button'
import { Badge } from '../ui/badge'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table'
import { aboveMedian, assetLabel, csvCell, formatGap, formatKPIValue, pick, type BenchmarkPoint, type Distribution } from './benchmark-utils'

/**
 * The scatter's table view, ranked by money above the median — and the only
 * view under `md`: a scatter does not read on a phone, a ranked list does.
 */
interface Props {
    distribution: Distribution
    locale: string
    onSelect: (point: BenchmarkPoint) => void
}

export function SpendRankedList({ distribution, locale, onSelect }: Props) {
    const { t, i18n } = useTranslation('benchmark')
    const { kpi, currency, stats } = distribution
    const lang = i18n.language?.startsWith('fr') ? 'fr' : 'en'

    const rows = useMemo(() => {
        const median = stats?.p50
        return distribution.points
            .map((p) => ({ point: p, money: median !== undefined ? aboveMedian(kpi.unit, p, median) : null }))
            .sort((a, b) => (b.money ?? -Infinity) - (a.money ?? -Infinity))
    }, [distribution.points, kpi.unit, stats?.p50])

    const money = (v: number | null) => {
        if (v === null) return '—'
        return currency
            ? new Intl.NumberFormat(locale, { style: 'currency', currency, maximumFractionDigits: 0, signDisplay: 'exceptZero' }).format(v)
            : new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(v)
    }
    const rank = (p: BenchmarkPoint) => {
        if (!p.rank) return '—'
        if (p.rank.decile !== null) return `D${p.rank.decile}`
        return `${p.rank.position} / ${p.rank.of}`
    }

    const exportCsv = () => {
        const header = ['code', 'name', 'building', 'value', 'unit', 'gap_to_median', 'above_median', 'rank', 'units']
        const lines = rows.map(({ point: p, money: m }) =>
            [
                p.asset.code,
                p.asset.name,
                p.asset.building ? assetLabel(p.asset.building) : '',
                p.value,
                kpi.unit === 'ratio' ? 'ratio' : `${currency}/${pick(kpi.denominator.label, 'en')}`,
                p.delta_vs_median ?? '',
                m === null ? '' : Math.round(m),
                rank(p),
                p.context.units ?? '',
            ]
                .map(csvCell)
                .join(';'),
        )
        const blob = new Blob([[header.join(';'), ...lines].join('\n')], { type: 'text/csv;charset=utf-8;' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `benchmark-${kpi.code}${distribution.breakdown_key ? `-${distribution.breakdown_key}` : ''}-${distribution.window.to}.csv`
        a.click()
        URL.revokeObjectURL(url)
    }

    return (
        <div className="space-y-2" data-testid="benchmark-ranked-list">
            <div className="flex items-center justify-between gap-2">
                <h3 className="text-sm font-medium text-foreground">{t('ranked.title')}</h3>
                <Button
                    variant="outline"
                    size="sm"
                    onClick={exportCsv}
                    aria-label={t('ranked.export')}
                    title={t('ranked.export')}
                    data-testid="benchmark-export-csv"
                >
                    <AppIcon path={mdiDownload} size="sm" className="md:mr-1" />
                    <span className="hidden md:inline">{t('ranked.export')}</span>
                </Button>
            </div>
            <div className="overflow-x-auto rounded-md border">
                {/* The table scrolls inside its own container on a phone rather
                    than squeezing the subject names into one word per line. */}
                <Table className="min-w-[40rem]">
                    <TableHeader>
                        <TableRow>
                            <TableHead>{t('ranked.subject')}</TableHead>
                            <TableHead className="text-right">{t('ranked.value')}</TableHead>
                            <TableHead className="text-right">{t('ranked.gap')}</TableHead>
                            <TableHead className="text-right">{t('ranked.above_median')}</TableHead>
                            <TableHead className="text-right">{t('ranked.rank')}</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {rows.map(({ point: p, money: m }) => (
                            <TableRow
                                key={p.asset.uid}
                                className="cursor-pointer"
                                onClick={() => onSelect(p)}
                                data-testid={`benchmark-row-${p.asset.code || p.asset.uid}`}
                            >
                                <TableCell>
                                    <div className="flex flex-wrap items-center gap-2">
                                        <span className="font-medium text-foreground">{assetLabel(p.asset)}</span>
                                        {p.outlier === 'high' && (
                                            <Badge
                                                variant="outline"
                                                className="border-warning bg-warning/15 text-foreground"
                                                title={t('scatter.legend_outliers')}
                                            >
                                                {t('ranked.outlier_badge')}
                                            </Badge>
                                        )}
                                        {p.open_issue_uid && <Badge variant="secondary">{t('ranked.open_issue')}</Badge>}
                                    </div>
                                    {p.asset.building && (
                                        <div className="text-xs text-muted-foreground">{assetLabel(p.asset.building)}</div>
                                    )}
                                </TableCell>
                                <TableCell className="text-right tabular-nums">{formatKPIValue(kpi.unit, p.value, currency, locale)}</TableCell>
                                <TableCell className="text-right tabular-nums">{formatGap(p.delta_vs_median, locale)}</TableCell>
                                <TableCell className="text-right tabular-nums">{money(m)}</TableCell>
                                <TableCell className="text-right tabular-nums text-muted-foreground">{rank(p)}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </div>
            <p className="text-xs text-muted-foreground">
                {pick(kpi.description, lang)}
            </p>
        </div>
    )
}
